import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { collection, orderBy, query, where } from "firebase/firestore";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { Bell } from "lucide-react";
import type { Session } from "@event-planner/shared";
import { FormPageLayout } from "../../components/forms/FormPageLayout";
import { FormSection } from "../../components/forms/FormSection";
import { QuestionsManager } from "../../components/forms/QuestionsManager";
import { db } from "../../lib/firebase";
import { useFirestoreCollectionOnce } from "../../hooks/useFirestoreCollectionOnce";
import { useCreateSurvey } from "../../hooks/useSurveyMutations";
import { useEvent } from "../../layouts/EventLayout";
import { useSnackbar } from "../../context/SnackbarContext";
import { useUnsavedChangesGuard } from "../../hooks/useUnsavedChangesGuard";
import { useIsPhone } from "../../hooks/useIsPhone";
import {
  createBlankQuestion,
  surveySchema,
  type SurveyFormData,
} from "../../schemas/surveySchema";

export function SurveyCreatePage() {
  const isPhone = useIsPhone();
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { event } = useEvent();
  const { showSuccess, showError } = useSnackbar();
  const { create, loading: submitting } = useCreateSurvey();
  const [tab, setTab] = useState("questions");
  const [serverError, setServerError] = useState<string | null>(null);

  const timezone = event?.timezone ?? "UTC";

  const form = useForm<SurveyFormData>({
    resolver: zodResolver(surveySchema),
    defaultValues: {
      questions: [createBlankQuestion()],
      sessionId: "",
      scheduleMode: "now",
      scheduledAt: null,
      sendNotification: true,
      notificationTitle: "",
      notificationBody: "",
    },
  });

  const { dialog: unsavedGuardDialog } = useUnsavedChangesGuard(
    form.formState.isDirty,
    form.formState.isSubmitting || submitting,
  );

  const watchScheduleMode = form.watch("scheduleMode");
  const watchSendNotification = form.watch("sendNotification");
  const watchNotificationTitle = form.watch("notificationTitle");
  const watchNotificationBody = form.watch("notificationBody");

  // ── Sessions for scope picker ──
  const sessionsQuery = useMemo(
    () =>
      eventId
        ? query(
            collection(db, "sessions"),
            where("eventId", "==", eventId),
            orderBy("startTime", "asc"),
          )
        : null,
    [eventId],
  );
  const { data: sessions } = useFirestoreCollectionOnce<Session>(sessionsQuery);

  async function onSubmit(data: SurveyFormData) {
    if (!eventId) return;
    setServerError(null);

    const questions = data.questions.map((q) => ({
      id: q.id,
      prompt: q.prompt,
      type: q.type,
      options:
        q.type === "single_choice"
          ? q.options
              .split("\n")
              .map((s) => s.trim())
              .filter(Boolean)
          : [],
      ratingAxisLabel: q.ratingAxisLabel,
      ratingScaleMin: q.ratingScaleMin,
      ratingScaleMax: q.ratingScaleMax,
      optionAxisLabel: q.optionAxisLabel,
      responseLabel: q.responseLabel,
      chartSubtitle: q.chartSubtitle,
    }));

    try {
      const { surveyId } = await create({
        eventId,
        sessionId: data.sessionId || null,
        questions,
        scheduleMode: data.scheduleMode,
        scheduledAt: data.scheduledAt,
        timezone,
        sendNotification: data.sendNotification,
        notificationTitle: data.notificationTitle,
        notificationBody: data.notificationBody,
      });

      showSuccess(
        data.scheduleMode === "now"
          ? "Survey created and opened"
          : "Survey scheduled",
      );
      navigate(`../${surveyId}`, { relative: "path" });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to create survey";
      setServerError(message);
      showError(message);
    }
  }

  return (
    <>
      {unsavedGuardDialog}
      <FormProvider {...form}>
        <FormPageLayout
          title="Create Survey"
          onSubmit={form.handleSubmit(onSubmit)}
          error={serverError}
          submitting={submitting}
          submitLabel={
            watchScheduleMode === "now" ? "Create & Open" : "Schedule"
          }
          testIdSlug="survey"
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: watchSendNotification ? "minmax(0, 1fr) 320px" : "1fr",
              },
              gap: 4,
            }}
          >
            {/* ── Left: tabbed form ── */}
            <Box>
              <Tabs
                value={tab}
                onChange={(_, v) => setTab(v)}
                variant={isPhone ? "scrollable" : "standard"}
                sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
              >
                <Tab label="Questions" value="questions" />
                <Tab label="Scheduling & Notification" value="scheduling" />
              </Tabs>

              {/* ── Questions tab ── */}
              {tab === "questions" && (
                <>
                  <FormSection title="Questions">
                    <QuestionsManager />
                  </FormSection>

                  <FormSection title="Scope">
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      Choose a session to bind this survey to a specific
                      agenda item, or leave blank for an event-wide survey.
                      Granular audience targeting (groups + people, matching
                      the notification compose page) is planned for a
                      follow-up.
                    </Typography>
                    <Controller
                      name="sessionId"
                      control={form.control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Session (optional)"
                          select
                          fullWidth
                          helperText="Leave blank for an event-wide survey"
                        >
                          <MenuItem value="">
                            <em>Event-wide (no specific session)</em>
                          </MenuItem>
                          {sessions.map((s) => (
                            <MenuItem key={s.id} value={s.id}>
                              {s.title}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  </FormSection>
                </>
              )}

              {/* ── Scheduling & Notification tab ── */}
              {tab === "scheduling" && (
                <>
                  <FormSection title="Timing">
                    <Controller
                      name="scheduleMode"
                      control={form.control}
                      render={({ field }) => (
                        <ToggleButtonGroup
                          value={field.value}
                          exclusive
                          onChange={(_, value) => {
                            if (value) {
                              field.onChange(value);
                              if (value === "now") {
                                form.setValue("scheduledAt", null);
                              }
                            }
                          }}
                          aria-label="Timing"
                          color="primary"
                        >
                          <ToggleButton value="now">Open now</ToggleButton>
                          <ToggleButton value="later">
                            Schedule for later
                          </ToggleButton>
                        </ToggleButtonGroup>
                      )}
                    />

                    {watchScheduleMode === "later" && (
                      <Controller
                        name="scheduledAt"
                        control={form.control}
                        render={({ field, fieldState }) => (
                          <TextField
                            {...field}
                            value={field.value ?? ""}
                            onChange={(e) =>
                              field.onChange(e.target.value || null)
                            }
                            type="datetime-local"
                            label={`Open at (${timezone})`}
                            required
                            fullWidth
                            error={!!fieldState.error}
                            helperText={
                              fieldState.error?.message ??
                              "The survey opens and the notification sends at this time"
                            }
                            slotProps={{ inputLabel: { shrink: true } }}
                          />
                        )}
                      />
                    )}
                  </FormSection>

                  <FormSection title="Push Notification">
                    <Controller
                      name="sendNotification"
                      control={form.control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={
                            <Switch
                              checked={field.value}
                              onChange={(e) =>
                                field.onChange(e.target.checked)
                              }
                            />
                          }
                          label="Send a push notification to announce this survey"
                        />
                      )}
                    />

                    {watchSendNotification && (
                      <>
                        <Controller
                          name="notificationTitle"
                          control={form.control}
                          render={({ field, fieldState }) => (
                            <TextField
                              {...field}
                              label="Notification title"
                              required
                              fullWidth
                              error={!!fieldState.error}
                              helperText={
                                fieldState.error?.message ??
                                "Headline shown on the lock screen"
                              }
                              inputProps={{ maxLength: 100 }}
                            />
                          )}
                        />

                        <Controller
                          name="notificationBody"
                          control={form.control}
                          render={({ field, fieldState }) => (
                            <TextField
                              {...field}
                              label="Notification body"
                              required
                              fullWidth
                              multiline
                              minRows={2}
                              error={!!fieldState.error}
                              helperText={
                                fieldState.error?.message ??
                                "Message body (max 500 chars)"
                              }
                              inputProps={{ maxLength: 500 }}
                            />
                          )}
                        />

                        <Typography variant="body2" color="text.secondary">
                          This notification will appear in the Notifications
                          tab alongside all other notifications for this
                          event. Targeting is automatic: session-scoped
                          surveys notify session attendees; event-wide
                          surveys notify all members.
                        </Typography>
                      </>
                    )}
                  </FormSection>
                </>
              )}
            </Box>

            {/* ── Right: push preview (only when notification is enabled).
                Phones stack it under the form, so show it only on the tab
                where the push title and body are edited. ── */}
            {watchSendNotification && (!isPhone || tab === "scheduling") && (
              <Box>
                <Typography
                  variant="overline"
                  color="text.secondary"
                  sx={{ display: "block", mb: 1 }}
                >
                  Push Preview
                </Typography>
                <Paper
                  elevation={2}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    bgcolor: "background.default",
                    border: 1,
                    borderColor: "divider",
                  }}
                >
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: 1.5,
                        bgcolor: "primary.main",
                        color: "primary.contrastText",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Bell size={20} />
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: "block" }}
                      >
                        {event?.name ?? "Flat Rock Events"}
                      </Typography>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 1,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {watchNotificationTitle || "Notification title"}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          whiteSpace: "pre-wrap",
                        }}
                      >
                        {watchNotificationBody ||
                          "Your message body will appear here."}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1.5, textAlign: "center" }}
                >
                  Approximate lock-screen appearance
                </Typography>
              </Box>
            )}
          </Box>
        </FormPageLayout>
      </FormProvider>
    </>
  );
}
